import type { ProductForSelector } from "@/shared/models/WorkOrder";
import { toFraction32 } from "@/utils/fraction";
import { Divider, Grid2, Paper, TextField, Typography } from "@mui/material";
import type { ReactNode } from "react";
import {
	type AssetType,
	type AssetTypeConfig,
	calcSawFields,
} from "../config/assetFieldConfig";

// Read-only spec panels keep disabled text at full contrast
export const SPECS_PANEL_SX = {
	p: 2,
	bgcolor: "grey.50",
	flexGrow: 1,
	"& .MuiInputBase-input.Mui-disabled": {
		WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
	},
	"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
		borderColor: "rgba(0, 0, 0, 0.23)",
	},
	"& .MuiInputLabel-root.Mui-disabled": {
		color: "rgba(0, 0, 0, 0.6)",
	},
} as const;

const HIGHLIGHT_SX = {
	"& .MuiOutlinedInput-root": { backgroundColor: "#e8f5e9" },
	"& .MuiInputBase-input.Mui-disabled": {
		WebkitTextFillColor: "#1b5e20",
		fontWeight: 700,
	},
} as const;

export const SectionTitle = ({
	children,
	mb = 0,
}: {
	children: ReactNode;
	mb?: number;
}) => (
	<Typography
		variant="overline"
		color="text.secondary"
		fontWeight={600}
		sx={{ display: "block", mb }}
	>
		{children}
	</Typography>
);

export const SpecField = ({
	label,
	value,
}: {
	label: string;
	value: string | number | null | undefined;
}) => (
	<TextField
		label={label}
		value={value ?? ""}
		size="small"
		fullWidth
		disabled
	/>
);

/** Product-derived numbers shared by every work-order surface. */
const useProductSpecs = ({
	product,
	config,
	assetType,
	quantity,
}: {
	product: ProductForSelector | null;
	config: AssetTypeConfig;
	assetType: AssetType;
	quantity: number | null;
}) => {
	const bundleCountValue = product
		? ((product[config.bundleCountField as keyof typeof product] as
				| number
				| null) ?? null)
		: null;

	const totalBundles =
		quantity && bundleCountValue && bundleCountValue > 0
			? Math.ceil(quantity / bundleCountValue)
			: null;

	const sawCalc =
		assetType === "saw" && product ? calcSawFields(product, quantity) : null;

	return { bundleCountValue, totalBundles, sawCalc };
};

type Props = {
	product: ProductForSelector | null;
	config: AssetTypeConfig;
	assetType: AssetType;
	quantity: number | null;
	/** `sm` breakpoint width of each spec field (12-col grid) */
	columns?: number;
	/** Per-field width overrides, e.g. keeping a length trio on its own row */
	fieldColumns?: Record<string, number>;
	/** `sm` width of the saw calculated fields; defaults to `columns` */
	sawColumns?: number;
	/** Render Pcs/Bundle + Total Bundles inside the panel instead of alongside it */
	showBundleTotals?: boolean;
	emptyMessage?: string;
};

export const ProductSpecsPanel = ({
	product,
	config,
	assetType,
	quantity,
	columns = 4,
	fieldColumns,
	sawColumns,
	showBundleTotals = false,
	emptyMessage = "Select a product to view specifications",
}: Props) => {
	const { bundleCountValue, totalBundles, sawCalc } = useProductSpecs({
		product,
		config,
		assetType,
		quantity,
	});

	if (!product) {
		return (
			<Paper variant="outlined" sx={SPECS_PANEL_SX}>
				<SectionTitle mb={2}>Product Specifications</SectionTitle>
				<Typography color="text.secondary" sx={{ py: 3 }} align="center">
					{emptyMessage}
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper variant="outlined" sx={SPECS_PANEL_SX}>
			<SectionTitle mb={2}>Product Specifications</SectionTitle>

			<Grid2 container spacing={1.5}>
				{config.productFields.map(
					({ field, label, format, highlight, fraction }) => {
						const raw = format
							? format(product)
							: // eslint-disable-next-line @typescript-eslint/no-explicit-any
								((product as any)[field] ?? "");
						const num = Number(raw);
						return (
							<Grid2
								key={field}
								size={{ xs: 6, sm: fieldColumns?.[field] ?? columns }}
							>
								<TextField
									label={label}
									value={raw}
									helperText={
										fraction && raw !== "" && Number.isFinite(num)
											? toFraction32(num)
											: undefined
									}
									size="small"
									fullWidth
									disabled
									sx={highlight ? HIGHLIGHT_SX : undefined}
								/>
							</Grid2>
						);
					},
				)}

				{showBundleTotals && (
					<>
						{/* Saw already lists Finish Pc.B among its product fields */}
						{!config.productFields.some(
							(f) => f.field === config.bundleCountField,
						) && (
							<Grid2 size={{ xs: 6, sm: columns }}>
								<SpecField
									label={config.bundleCountLabel}
									value={bundleCountValue}
								/>
							</Grid2>
						)}
						<Grid2 size={{ xs: 6, sm: columns }}>
							<SpecField label="Total Bundles" value={totalBundles} />
						</Grid2>
					</>
				)}
			</Grid2>

			{sawCalc && (
				<>
					<Divider sx={{ my: 2 }} />
					<Grid2 container spacing={1.5}>
						{[
							["# MLB", sawCalc.numMLB],
							["# Tags", sawCalc.numTags],
							["Overcut", sawCalc.overcut],
							["Cuts/Bundle", sawCalc.cutsPerBundle],
							["Pcs/Mill Bundle", sawCalc.pcsPerMillBundle],
							// Already shown above when the panel owns the bundle totals
							...(showBundleTotals
								? []
								: [["Pcs/Bundle", bundleCountValue] as const]),
						].map(([label, value]) => (
							<Grid2
								key={label as string}
								size={{ xs: 6, sm: sawColumns ?? columns }}
							>
								<SpecField
									label={label as string}
									value={value as number | null}
								/>
							</Grid2>
						))}
					</Grid2>
				</>
			)}
		</Paper>
	);
};
